import React, { SetStateAction, useEffect, useState } from "react";
import styled from "styled-components";
import { AppText, FlexDiv } from "../../../ui";
import { useOnboardingContext } from "../OnboardingContext";
import theme from "../../../../util/theme";
import { mobileSize } from "../../../../util/variables";
import { web_acq_question_click } from "../../../../services/mixpanel/acquisition-events";
import FlameLarge from "../../../../content/assets/flame-large.svg";
import FlameMedium from "../../../../content/assets/flame-medium.svg";
import FlameSmall from "../../../../content/assets/flame-small.svg";

export const GOAL_OPTIONS = {
  Quick: {
    label: "Quick",
    minutes: 2,
    num_lessons_per_month: 30,
    flame_asset: {
      src: FlameSmall,
      width: 29,
      height: 44,
    },
  },
  Regular: {
    label: "Regular",
    minutes: 5,
    num_lessons_per_month: 75,
    flame_asset: {
      src: FlameMedium,
      width: 38,
      height: 52,
    },
  },
  Advanced: {
    label: "Advanced",
    minutes: 10,
    num_lessons_per_month: 150,
    flame_asset: {
      src: FlameLarge,
      width: 41,
      height: 57,
    },
  },
};

interface SpecialSelectQuestionI {
  id: string;
  answer_key: string;
  variant?: boolean;
}

const SpecialSelectQuestion: React.FC<SpecialSelectQuestionI> = ({ id, answer_key, variant }) => {
  const [choice, setchoice] = useState("");
  const { updateOnboardingAnswer } = useOnboardingContext();

  useEffect(() => {
    if (!choice.length) {
      return;
    }

    const selectedOpt = Object.values(GOAL_OPTIONS).find(opt => opt.label === choice);
    updateOnboardingAnswer(answer_key, selectedOpt?.label);
  }, [choice]);

  return (
    <FlexDiv direction="column" align={variant ? "flex-start" : "center"} style={{ maxWidth: "100vw", textAlign: "center", height: "100%" }}>
      <TextWrapper>
        <AppText noMargin fontSize={24} mobileFontSize={20} fontWeight={500} style={{ maxWidth: 468 }}>
          Set a daily learning goal
        </AppText>
        <AppText noMargin fontSize={16} mobileFontSize={14} style={{ maxWidth: 468, marginTop: 12 }}>
          You can always change this later.
        </AppText>
      </TextWrapper>
      <OptionsContainer gap={24} mobileGap={16}>
        <Option
          id={id}
          label="Quick"
          minutes={2}
          updateChoice={setchoice}
          selected={choice === "Quick"}
        />
        <Option
          id={id}
          label="Regular"
          minutes={5}
          updateChoice={setchoice}
          selected={choice === "Regular"}
        />
        <Option
          id={id}
          label="Advanced"
          minutes={10}
          updateChoice={setchoice}
          selected={choice === "Advanced"}
        />
      </OptionsContainer>
      {variant && !!GOAL_OPTIONS[choice] && (
        <VariantContainer>
          <VariantContent gap={16}>
            <FlameContainer>
              <img
                src={GOAL_OPTIONS[choice]!.flame_asset.src}
                alt="bite sized lessons"
                width={GOAL_OPTIONS[choice]!.flame_asset.width}
                height={GOAL_OPTIONS[choice]!.flame_asset.height}
              />
            </FlameContainer>
            <AppText noMargin fontSize={18} mobileFontSize={18} textAlign="left" style={{ maxWidth: 300 }}>
              That&apos;s <span style={{ fontWeight: 500 }}>{GOAL_OPTIONS[choice]!.num_lessons_per_month}</span> bite-sized lessons in a month!
            </AppText>
          </VariantContent>
        </VariantContainer>
      )}
    </FlexDiv>
  );
};

const TextWrapper = styled.div`
  padding: 0 10px;
  text-align: center;
  margin: 0 auto;
  @media ${mobileSize} {
    padding: 0 32px;
  }
`;

const OptionsContainer = styled(FlexDiv as any)`
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  flex-grow: 1;
  gap: 24px;
  margin-top: 60px;
  padding: 12px;
  height: 100%;
  max-width: 100vw;
  overflow: auto;
  @media ${mobileSize} {
    margin-top: 24px;
    padding: 8px 32px;
    max-height: calc(100% - 60px); // height of text + margins
    gap: 10px;
  }
`;

const VariantContainer = styled(FlexDiv)`
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 468px;
  padding: auto 12px 16px 12px;
  margin: 0 6px;
  @media ${mobileSize} {
    padding: 12px 32px;
    margin: 0;
  }
`;

const VariantContent = styled(FlexDiv)`
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding: 16px 14px;
  border: 2px solid #FFF48F;
  border-radius: 8px;
  box-sizing: border-box;
  background-color: #FFF48F66;
`;

const FlameContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 72px;
  height: 72px;
  padding-bottom: 12px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: 0.5px solid ${theme.BLACK_COLOR};
  background: radial-gradient(circle, #ff0000aa 0%, #FFFFFF 70%);
`;

interface OptionProps {
  id: string;
  label: string;
  minutes: number;
  selected: boolean;
  updateChoice: React.Dispatch<SetStateAction<string>>;
}

const Option: React.FC<OptionProps> = ({ id, label, minutes, updateChoice, selected }) => {
  return (
    <Container
      selected={selected}
      onClick={() => {
        updateChoice(label);
        web_acq_question_click(id);
      }}
    >
      <OptionText style={{ fontWeight: 500 }}>{label}</OptionText>
      <Separator selected={selected} />
      <OptionText>{minutes} min / day</OptionText>
    </Container>
  );
};

const Container = styled.div<{ selected: boolean }>`
  display: grid;
  grid-template-columns: 1fr 2px 2fr;
  align-items: center;
  /* justify-content: center; */

  width: 468px;
  max-width: 100%;
  height: 86px;
  box-sizing: border-box;

  background-color: ${props => (props.selected ? theme.SELECTED_COLOR : theme.WHITE_COLOR)};
  outline: ${props => (props.selected ? `1px solid ${theme.SELECTED_BORDER_COLOR}` : "none")};
  border-radius: 8px;
  box-shadow: ${props =>
    props.selected ? "0px 1px 7px rgba(44, 39, 56, 0.14)" : "0px 2px 11px rgba(44, 39, 56, 0.14)"};
  cursor: pointer;
  @media ${mobileSize} {
    height: 64px;
    /* width: 300px; */
  }
`;

const OptionText = styled.p`
  margin: 0;
  font-family: ${theme.SECONDARY_FONT};
  font-size: 20px;

  @media ${mobileSize} {
    font-size: 14px;
  }
`;

const Separator = styled.div<{ selected: boolean }>`
  height: 32px;
  width: 2px;
  border-radius: 16px;
  background-color: ${props => (props.selected ? theme.SELECTED_BORDER_COLOR : theme.GREY_COLOR)};
`;

// const Img = styled.img`
//   height: 768px;
//   max-height: 768px;
// `;

export default SpecialSelectQuestion;
